import React, { useEffect, useState } from 'react';
import { useApps } from '@/contexts/AppsContext';
import { useAppId } from '@/hooks/useAppId';
import { billingApi } from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatNumber, formatBytes, formatCurrency } from '@/lib/utils';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { CreditCard, TrendingUp, Check, Zap, ExternalLink } from 'lucide-react';

const PLAN_COLORS = {
  free: 'bg-zinc-500',
  starter: 'bg-blue-500',
  pro: 'bg-purple-500',
  enterprise: 'bg-amber-500',
};

export default function BillingPage() {
  const appId = useAppId();
  const { currentApp } = useApps();
  const [usage, setUsage] = useState(null);
  const [usageHistory, setUsageHistory] = useState([]);
  const [sourceBreakdown, setSourceBreakdown] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [period, setPeriod] = useState('month');

  const PERIODS = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: '6months', label: '6 Months' },
    { value: 'year', label: 'Year' },
    { value: 'ytd', label: 'YTD' },
  ];

  // Fetch plans on mount
  useEffect(() => {
    billingApi.getPlans()
      .then(res => setPlans(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(err => console.error('Failed to fetch plans:', err))
      .finally(() => setLoading(false));
  }, []);

  // Fetch usage + history when period or appId changes
  useEffect(() => {
    if (!appId) return;
    billingApi.getUsage(appId, period)
      .then(res => {
        const data = res.data?.data || {};
        setUsage(data);
        setSourceBreakdown(data.sources || []);
      })
      .catch(err => console.error('Failed to fetch usage:', err));

    billingApi.getUsageHistory(appId, period)
      .then(res => {
        const data = res.data?.data || {};
        setUsageHistory(data.history || []);
        if (!sourceBreakdown.length) setSourceBreakdown(data.sources || []);
      })
      .catch(err => console.error('Failed to fetch usage history:', err));
  }, [appId, period]);

  const handleUpgrade = async (planTier) => {
    if (!currentApp) return;

    setUpgrading(true);
    try {
      const response = await billingApi.createCheckout(
        appId,
        planTier,
        `${window.location.origin}/billing?success=true`,
        `${window.location.origin}/billing?canceled=true`
      );

      if (response.data.data?.checkoutUrl) {
        window.location.href = response.data.data.checkoutUrl;
      }
    } catch (err) {
      console.error('Failed to create checkout:', err);
    } finally {
      setUpgrading(false);
    }
  };

  const handleManageBilling = async () => {
    if (!currentApp) return;

    try {
      const response = await billingApi.createBillingPortal(
        appId,
        window.location.href
      );

      if (response.data.data?.portalUrl) {
        window.location.href = response.data.data.portalUrl;
      }
    } catch (err) {
      console.error('Failed to open billing portal:', err);
    }
  };

  // Compute period totals from history data
  const periodTotals = usageHistory.reduce((acc, item) => ({
    apiCalls: acc.apiCalls + (item.apiCalls || 0),
    computeUnits: acc.computeUnits + (item.computeUnits || 0),
    llmInputTokens: acc.llmInputTokens + (item.llmInputTokens || 0),
    llmOutputTokens: acc.llmOutputTokens + (item.llmOutputTokens || 0),
    entitiesCreated: acc.entitiesCreated + (item.entitiesCreated || 0),
    processingMs: acc.processingMs + (item.processingMs || 0),
  }), { apiCalls: 0, computeUnits: 0, llmInputTokens: 0, llmOutputTokens: 0, entitiesCreated: 0, processingMs: 0 });

  // Prepare chart data
  const chartData = usageHistory
    .map((item) => ({
      name: item.date || item.periodKey,
      apiCalls: item.apiCalls || 0,
      tokens: (item.llmInputTokens || 0) + (item.llmOutputTokens || 0),
    }))
    .reverse();

  const currentPlan = usage?.planTier || 'free';
  const currentPlanConfig = plans.find((p) => p.tier === currentPlan);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="usage" className="space-y-6">
        <TabsList>
          <TabsTrigger value="usage">Usage</TabsTrigger>
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
        </TabsList>

        {/* Usage Tab */}
        <TabsContent value="usage" className="space-y-6">
          {/* Current Plan Banner */}
          <Card>
            <CardContent className="flex items-center justify-between py-4">
              <div className="flex items-center space-x-4">
                <div
                  className={`h-12 w-12 rounded-lg ${PLAN_COLORS[currentPlan]} flex items-center justify-center`}
                >
                  <Zap className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold capitalize">{currentPlan} Plan</h3>
                  <p className="text-sm text-muted-foreground">
                    {currentPlan === 'free'
                      ? 'Free forever'
                      : `${formatCurrency(currentPlanConfig?.price || 0)}/month`}
                  </p>
                </div>
              </div>
              {currentPlan !== 'enterprise' && (
                <Button onClick={() => handleUpgrade('pro')} disabled={upgrading}>
                  {upgrading ? 'Loading...' : 'Upgrade Plan'}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Period Selector */}
          <div className="flex items-center gap-1 p-1 bg-muted rounded-lg w-fit">
            {PERIODS.map(p => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  period === p.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Usage Stats — scoped to selected period */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  API Calls
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatNumber(usage?.apiCalls || 0)}
                </div>
                <Progress
                  value={usage?.limits?.apiCalls ? (usage.apiCalls / usage.limits.apiCalls) * 100 : 0}
                  className="mt-2 h-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  of {formatNumber(usage?.limits?.apiCalls || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Tokens
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatNumber(usage?.llmTokens || 0)}
                </div>
                <Progress
                  value={
                    usage?.limits?.llmTokens
                      ? ((usage?.llmTokens || 0) / usage.limits.llmTokens) * 100
                      : 0
                  }
                  className="mt-2 h-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  of {formatNumber(usage?.limits?.llmTokens || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Storage <span className="text-[10px] font-normal">(current)</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatBytes(usage?.storageBytes || 0)}
                </div>
                <Progress value={usage?.storagePercent || 0} className="mt-2 h-1" />
                <p className="text-xs text-muted-foreground mt-1">
                  of {usage?.limits?.storageGb || 0} GB
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Processing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {(usage?.processingMinutes || 0).toFixed(1)} min
                </div>
                <Progress
                  value={
                    usage?.limits?.processingMinutes
                      ? ((usage?.processingMinutes || 0) /
                          usage.limits.processingMinutes) *
                        100
                      : 0
                  }
                  className="mt-2 h-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  of {usage?.limits?.processingMinutes || 0} min
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Usage Charts */}
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>API Calls & Tokens</CardTitle>
                <CardDescription>{PERIODS.find(p => p.value === period)?.label || 'Month'} usage</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorApiCalls" x1="0" y1="0" x2="0" y2="1">
                          <stop
                            offset="5%"
                            stopColor="hsl(var(--primary))"
                            stopOpacity={0.3}
                          />
                          <stop
                            offset="95%"
                            stopColor="hsl(var(--primary))"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="name" className="text-xs" />
                      <YAxis className="text-xs" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="apiCalls"
                        stroke="hsl(var(--primary))"
                        fillOpacity={1}
                        fill="url(#colorApiCalls)"
                        name="API Calls"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Token Usage</CardTitle>
                <CardDescription>LLM tokens consumed</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="name" className="text-xs" />
                      <YAxis className="text-xs" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend />
                      <Bar
                        dataKey="tokens"
                        fill="hsl(47, 100%, 50%)"
                        name="Tokens"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-4">
            {plans.map((plan) => {
              const isCurrentPlan = plan.tier === currentPlan;
              const isPopular = plan.tier === 'pro';

              return (
                <Card
                  key={plan.tier}
                  className={`relative ${isCurrentPlan ? 'border-primary' : ''} ${
                    isPopular ? 'shadow-lg' : ''
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary">Most Popular</Badge>
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      {plan.name}
                      {isCurrentPlan && (
                        <Badge variant="outline">Current</Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      <span className="text-3xl font-bold">
                        {plan.price === 'custom'
                          ? 'Custom'
                          : plan.price === 0
                          ? 'Free'
                          : formatCurrency(plan.price)}
                      </span>
                      {plan.price !== 'custom' && plan.price > 0 && (
                        <span className="text-muted-foreground">/month</span>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center">
                        <Check className="mr-2 h-4 w-4 text-green-500" />
                        {plan.limits.apps === 'unlimited'
                          ? 'Unlimited apps'
                          : `${plan.limits.apps} app${plan.limits.apps > 1 ? 's' : ''}`}
                      </li>
                      <li className="flex items-center">
                        <Check className="mr-2 h-4 w-4 text-green-500" />
                        {plan.limits.apiCalls === 'unlimited'
                          ? 'Unlimited API calls'
                          : `${formatNumber(plan.limits.apiCalls)} API calls`}
                      </li>
                      <li className="flex items-center">
                        <Check className="mr-2 h-4 w-4 text-green-500" />
                        {plan.limits.storageGb === 'unlimited'
                          ? 'Unlimited storage'
                          : `${plan.limits.storageGb} GB storage`}
                      </li>
                      <li className="flex items-center">
                        <Check className="mr-2 h-4 w-4 text-green-500" />
                        {plan.limits.llmTokens === 'unlimited'
                          ? 'Unlimited LLM tokens'
                          : `${formatNumber(plan.limits.llmTokens)} LLM tokens`}
                      </li>
                    </ul>
                    {!isCurrentPlan && (
                      <Button
                        className="w-full"
                        variant={isPopular ? 'default' : 'outline'}
                        onClick={() => handleUpgrade(plan.tier)}
                        disabled={upgrading || plan.tier === 'enterprise'}
                      >
                        {plan.tier === 'enterprise' ? 'Contact Sales' : 'Upgrade'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CreditCard className="h-5 w-5" />
                <span>Billing Management</span>
              </CardTitle>
              <CardDescription>
                Manage your payment methods, view invoices, and update billing
                information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Click below to access the Stripe billing portal where you can:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>Update payment methods</li>
                <li>View and download invoices</li>
                <li>Change your subscription</li>
                <li>Cancel your subscription</li>
              </ul>
              <Button onClick={handleManageBilling}>
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Billing Portal
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
